import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import useGameTeamsQuery from '@/features/games/hooks/useGameTeamsQuery';

import { HookFormDatePicker, HookFormSelect } from '../../forms/components';
import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { ClipsFormValues } from '../hooks/useAllClipsQuery';

type Props = {
  onChange: (values: ClipsFormValues) => unknown;
};


export const DEFAULT_VALUES: ClipsFormValues = {
  date: null,
  team: null,
  status: null,
  clipType: 'clip',
};

const ClipsForm = ({ onChange }: Props) => {
  const { currentLeague } = useUserStateContext();
  const methods = useForm<ClipsFormValues>({ defaultValues: DEFAULT_VALUES });
  const { watch, reset } = methods;
  const teamsQuery = useGameTeamsQuery(currentLeague?.id, undefined, { enabled: true });

  // Fetch teams data on mount
  useEffect(() => {
    if (currentLeague?.id) {
      teamsQuery.refetch();
    }
  }, [currentLeague?.id]);

  // submits the form onChange
  useEffect(() => {
    const subscription = watch((data) => {
      onChange(data as ClipsFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange]);

  const handleReset = () => {
    reset(DEFAULT_VALUES);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: 3,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <HookFormDatePicker
            name="date"
            TextFieldProps={{
              label: 'Date',
              sx: { width: { xs: '100%', sm: '300px' } }
            }}
            DatePickerProps={{ label: 'Date' }}
          />

          <Controller
            name="team"
            control={methods.control}
            render={({ field }) => (
              <Autocomplete
                onChange={(_e, v) => {
                  field.onChange(v?.abbreviation || '');
                }}
                isOptionEqualToValue={(option, value) => option.abbreviation === value}
                value={teamsQuery.data?.find(t => t.abbreviation === field.value) || null}
                id="team-selector"
                getOptionLabel={(option: any) =>
                  option?.abbreviation ? option.abbreviation : ''
                }
                options={teamsQuery.data || []}
                renderOption={(props, option: any) => (
                  <Box
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...props}
                  >
                    <img
                      loading="lazy"
                      width="35"
                      src={getTeamLogoUrl(option)}
                      alt={option.abbreviation}
                    />
                    {option.abbreviation}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Team"
                    sx={{
                      width: { xs: '100%', sm: '300px' }
                    }}
                  />
                )}
              />
            )}
          />

          <HookFormSelect
            name="status"
            label="Status"
            sx={{
              width: '200px',
              minWidth: '200px'
            }}
            onChange={(e) => {
              if (e.target.value === '') {
                methods.setValue('status', null);
              }
              else {
                methods.setValue('status', e.target.value);
              }
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="READY">Ready</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="ERROR">Error</MenuItem>
            <MenuItem value="CANCELED">Canceled</MenuItem>
            <MenuItem value="NEW">New</MenuItem>
          </HookFormSelect>

          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            color="secondary"
            sx={{ minWidth: '150px', whiteSpace: 'nowrap' }}
          >
            Reset Filters
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default ClipsForm;